import type { LedgerReportAccount, ReportRequestBody } from '@query'
import { useReportEntries } from '@query'
import React, { useEffect, useMemo, useState } from 'react'
import { AccountEntriesPanel } from './AccountEntriesPanel'
import { drillEntryParams } from './drillEntryParams'

type Props = {
  slug: string
  reportBody: ReportRequestBody
  accountNumber: string
  currency?: string
}

export const LedgerAccountEntriesPanel: React.FC<Props> = ({
  slug,
  reportBody,
  accountNumber,
  currency
}) => {
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [accountNumber])

  const params = useMemo(
    () => drillEntryParams(reportBody, { account_number: accountNumber }, page),
    [reportBody, accountNumber, page]
  )

  const { data, isFetching, isLoading } = useReportEntries({
    slug,
    params,
    enabled: Boolean(slug && accountNumber)
  })

  const account: LedgerReportAccount | undefined = useMemo(() => {
    if (!data) return undefined
    return {
      number: data.meta.account_number ?? accountNumber,
      name: data.meta.account_name ?? '',
      opening_balance: data.meta.page_opening_balance ?? data.meta.opening_balance ?? 0,
      entries: data.results.map(entry => ({
        number: entry.number,
        date: entry.date,
        debet: entry.debet,
        credit: entry.credit,
        balance_csum: entry.balance_csum ?? 0,
        description: entry.description,
        document_id: entry.document_id
      })),
      entries_count: data.count,
      totals: {
        debet: data.meta.debet_total,
        credit: data.meta.credit_total,
        balance_csum: data.meta.closing_balance ?? 0
      }
    }
  }, [data, accountNumber])

  const showLoading = isLoading || isFetching

  return (
    <AccountEntriesPanel
      account={account}
      attachmentsByDocument={data?.attachments_by_document}
      currency={currency}
      isLoading={showLoading}
      entriesPage={page}
      entriesPageCount={data?.page_count ?? 1}
      entriesTotalCount={data?.count ?? 0}
      entriesPageSize={data?.page_size ?? params.page_size}
      onEntriesPageChange={setPage}
    />
  )
}
